import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate, Outlet, useMatches } from 'react-router-dom';
import { $api } from '../api';
import { useAuth } from '../App';

interface Organization {
  id: string;
  name: string;
  role: 'owner' | 'admin' | 'member';
  pending_alerts_count?: number;
  sandbox_mode: boolean;
  joined_at: string;
  created_at: string;
  updated_at: string;
}

interface OrganizationContextType {
  currentOrganization: Organization | null;
  organizations: Organization[];
  isLoading: boolean;
  switchOrganization: (orgId: string, targetPath?: string | null, organizations?: Organization[]) => void;
  refreshOrganizations: () => Promise<Organization[]>;
}

const OrganizationContext = createContext<OrganizationContextType>({
  currentOrganization: null,
  organizations: [],
  isLoading: true,
  switchOrganization: () => {},
  refreshOrganizations: () => Promise.resolve([]),
});

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context
};

interface OrganizationProviderProps {
  children?: ReactNode;
  isAuthenticated?: boolean;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ 
  children, 
  isAuthenticated 
}) => {
  const { isAuthenticated: isAuthenticatedAuth } = useAuth();
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const navigate = useNavigate();
  const matches = useMatches()
  children = children ?? <Outlet />
  isAuthenticated = isAuthenticated ?? isAuthenticatedAuth;

  // useQuery for organizations list
  const organizationsQuery = $api.useQuery('get', '/api/organizations', {}, { 
    enabled: isAuthenticated,
  });

  const organizations = organizationsQuery.data?.organizations || [];

  // Sync current organization with URL parameter
  useEffect(() => {
    if (!isAuthenticated) {
      setCurrentOrganization(null);
      return;
    }

    if (organizationsQuery.data?.organizations && organizationsQuery.data.organizations.length > 0) {
      const availableOrgs = organizationsQuery.data.organizations;

      // set organization id from url parameter
      const matchOrgId = matches.find(match => match.params.organizationId)?.params.organizationId;
      if(matchOrgId && availableOrgs.find(org => org.id === matchOrgId)) {
        if(!currentOrganization || currentOrganization.id !== matchOrgId) {
          setCurrentOrganization(availableOrgs.find(org => org.id === matchOrgId)!);
        }

        return;
      }

      // set organization id from local storage
      const savedOrgId = localStorage.getItem('current_organization_id')
      if(savedOrgId && !currentOrganization) {
        const org = availableOrgs.find(org => org.id === savedOrgId);
        if(org) {
          setCurrentOrganization(org);
          return
        }
      }

      if(!currentOrganization) {
        setCurrentOrganization(availableOrgs[0]);
      }
    }
  }, [isAuthenticated, organizationsQuery.data?.organizations, currentOrganization, matches]);

  useEffect(() => {
    if(currentOrganization) {
      localStorage.setItem('current_organization_id', currentOrganization.id);
    }
  }, [currentOrganization]);

  const switchOrganization = (orgId: string, targetPath: string | null = '/dashboard', norganizations?: Organization[]) => {
    const org = (norganizations || organizations).find(o => o.id === orgId);
    if (org && (!currentOrganization || currentOrganization.id !== orgId)) {
      setCurrentOrganization(org);
      localStorage.setItem('current_organization_id', org.id);

      if(targetPath) {
        const newPath = `/${org.id}${targetPath}`;
        navigate(newPath);
      }
    }
  };

  const refreshOrganizations = async () => {
    const res = await organizationsQuery.refetch();
    return res.data?.organizations || [];
  };

  return (
    <OrganizationContext.Provider
      value={{
        currentOrganization,
        organizations,
        isLoading: organizationsQuery.isLoading,
        switchOrganization,
        refreshOrganizations,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};